import React, { FC, useMemo, memo } from 'react';
import { useTranslation } from '@blocs.i18n';
import { PickButton } from '@minecraft.molecules';
import { ID } from '../../../shared/types';
import { usePickAdd, usePickDelete, FeedbackSectionPick, HandleUpdateSubmissionFn } from '../../FeedbackSection';
import { getPickTooltipContent } from '../PickButtonUtils';

type Props = {
  isPublic: boolean;
  withTooltip?: boolean;
  entityId: ID;
  submissionId: ID;
  presentationId: ID;
  folderGuid: string;
  userName?: string;
  pickCount: number;
  myPick?: FeedbackSectionPick;
  pickedBy: FeedbackSectionPick[];
  handleUpdateSubmission?: HandleUpdateSubmissionFn;
  handleUserNameModalToggle?: () => void;
  qaId?: string;
  fullWidth?: boolean;
  getFeedbackData?: never;
  className?: never;
};

const PickButtonCD: FC<Props> = ({
  isPublic = false,
  fullWidth = true,
  withTooltip = false,
  entityId,
  submissionId,
  presentationId,
  folderGuid,
  userName,
  pickCount,
  myPick: myPickProp,
  pickedBy = [],
  handleUpdateSubmission,
  handleUserNameModalToggle,
  qaId,
}) => {
  const { t } = useTranslation();

  /** pick mutations  */
  const { addPickSubmitting, onPickAdd } = usePickAdd({
    isPublic,
    presentationId,
    submissionId,
    talentEntityId: entityId,
    pickedBy,
    handleUpdateSubmission,
    userName,
  });
  const { deletePickSubmitting, onPickDelete } = usePickDelete({
    isPublic,
    handleUpdateSubmission,
    pickedBy,
    folderGuid,
    submissionId,
    talentEntityId: entityId,
    userName,
  });

  const { pickedByNames, pickButtonLabel, onPickButtonClick, mutationSubmitting } = useMemo(() => {
    const myUsersPickMemo = myPickProp || pickedBy?.find((pick) => pick?.userName === userName);

    return {
      myPick: myUsersPickMemo,
      pickedByNames: getPickTooltipContent({ myPick: myUsersPickMemo, pickedBy }, t),
      pickButtonLabel: myUsersPickMemo ? t('common:button.unpickTalent') : t('common:button.pickTalent'),
      onPickButtonClick: async () => {
        if (!userName) {
          handleUserNameModalToggle();
          return;
        }

        if (myUsersPickMemo) {
          await onPickDelete({ pickId: myUsersPickMemo?.id });
        } else {
          await onPickAdd();
        }
      },
      mutationSubmitting: addPickSubmitting || deletePickSubmitting,
    };
  }, [
    addPickSubmitting,
    deletePickSubmitting,
    handleUserNameModalToggle,
    myPickProp,
    onPickAdd,
    onPickDelete,
    pickedBy,
    t,
    userName,
  ]);

  return (
    <PickButton
      id={`pick-button-${entityId}`}
      picksCount={pickCount?.toString()}
      disabled={mutationSubmitting}
      onClick={onPickButtonClick}
      pickedByNames={pickedByNames}
      aria-label={pickButtonLabel}
      data-qa-id={qaId}
      withTooltip={withTooltip}
      fixedWidth={fullWidth}
      full={fullWidth}
    />
  );
};

export default memo(PickButtonCD);
